import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { today, addDays, prettyDay, KINDS } from "@/lib/perform";
import { WodEditor } from "@/components/perform/WodParts";
import { ChevronLeft, ChevronRight, Plus, Sparkles, Tv, Pencil, Trash2, Send, Star, Loader2 } from "lucide-react";

const monday = (d) => {
  const [y, m, dd] = d.split("-").map(Number);
  const x = new Date(y, m - 1, dd);
  const off = (x.getDay() + 6) % 7;
  return addDays(d, -off);
};

function DayColumn({ day, onAdd, onEdit, refreshKey }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["wods", day, refreshKey], queryFn: async () => (await api.get("/api/wods", { params: { day } })).data });
  const club = (data?.wods || []).filter((w) => w.scope === "club");
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["wods"] }); } catch (err) { toast.error(apiErr(err)); } };
  const isToday = day === today();
  return (
    <div className={`rounded-xl border p-3 min-h-[12rem] flex flex-col gap-2 ${isToday ? "border-acid/60 bg-acid/5" : "border-[#1E222A] bg-[#0F1115]"}`} data-testid={`day-${day}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="mono-label">{prettyDay(day, { weekday: "short" })}</p>
          <p className="font-heading font-black text-lg leading-none">{prettyDay(day, { day: "numeric", month: "short" })}</p>
        </div>
        <button onClick={() => onAdd(day)} className="p-1.5 rounded-full border border-[#343A4A] text-acid hover:bg-acid hover:text-black" aria-label="Add workout"><Plus className="w-4 h-4" /></button>
      </div>
      {club.length === 0 && <p className="text-xs text-[#565C6F] mt-2">Rest day or not programmed yet.</p>}
      {club.map((w) => (
        <div key={w.id} className="rounded-lg border border-[#1E222A] bg-[#0B0D11] p-2.5 space-y-1.5">
          <span className={`inline-block mono-label rounded-full px-1.5 py-0.5 ${w.status === "draft" ? "text-[#FFB340] bg-[#FFB340]/10" : "text-acid bg-acid/10"}`}>{w.status === "draft" ? (w.source === "octon" ? "octon draft" : "draft") : "live"}</span>
          <p className="text-sm font-semibold leading-tight break-words">{w.title}</p>
          <div className="flex flex-wrap gap-1">
            {(w.sections || []).map((s, i) => <span key={i} className="w-2 h-2 rounded-full" title={s.title} style={{ background: (KINDS[s.kind] || KINDS.metcon).c }} />)}
          </div>
          {w.score_count ? <p className="text-xs text-[#8A91A6]">{w.score_count} scores</p> : null}
          <div className="flex gap-1">
            <button onClick={() => onEdit(w)} className="p-1.5 text-[#8A91A6] hover:text-white" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
            {w.status === "draft" && <button onClick={() => act(() => api.post(`/api/wods/${w.id}/publish`), "Published")} className="p-1.5 text-acid" aria-label="Publish" data-testid="publish"><Send className="w-3.5 h-3.5" /></button>}
            <button onClick={() => window.confirm("Delete this workout and its scores?") && act(() => api.delete(`/api/wods/${w.id}`), "Deleted")} className="p-1.5 text-[#8A91A6] hover:text-pulseError ml-auto" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Feedback() {
  const { data } = useQuery({ queryKey: ["club-feedback"], queryFn: async () => (await api.get("/api/club/feedback")).data });
  if (!data) return null;
  return (
    <div className="surface rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-bold">Session feedback</p>
        {data.average && <span className="inline-flex items-center gap-1 text-[#FFB340] text-sm"><Star className="w-4 h-4 fill-[#FFB340]" /> {data.average} average</span>}
      </div>
      {data.items.length === 0 ? <p className="text-sm text-[#8A91A6]">Athletes can rate each session when they log their score. Ratings appear here.</p> : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {data.items.map((f, i) => (
            <div key={i} className="rounded-lg border border-[#1E222A] p-3">
              <div className="flex justify-between text-sm"><span>{f.name} · <span className="text-[#8A91A6]">{f.wod_title}, {f.wod_date}</span></span>
                {f.rating && <span className="text-[#FFB340]">{"★".repeat(f.rating)}</span>}</div>
              {f.text && <p className="text-sm text-[#D7DCE5] mt-1">"{f.text}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Programming() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [week, setWeek] = useState(monday(today()));
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState({ focus: "", level: "mixed", days: 5 });
  const [busy, setBusy] = useState(false);
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const runDraft = async () => {
    setBusy(true);
    try {
      const start = week > today() ? week : addDays(today(), 1);  // never draft into days already gone
      const { data } = await api.post("/api/club/program/draft", { start, days: Number(draft.days), focus: draft.focus, level: draft.level });
      toast.success(`OCTON drafted ${data.created.length} workouts. Review and publish them.`);
      qc.invalidateQueries({ queryKey: ["wods"] });
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };
  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.strength} overline="club programming" title="Program your week"
        subtitle="Write each day's workout, choose how it's scored and when athletes see it. Or let OCTON draft the week for you to approve."
        actions={<button onClick={() => navigate("/tv")} className="inline-flex items-center gap-2 rounded-full h-10 px-5 border border-white/30 backdrop-blur" data-testid="open-tv"><Tv className="w-4 h-4" /> Gym TV</button>} />

      <div className="relative overflow-hidden rounded-2xl border border-acid/30 bg-gradient-to-br from-[#12160A] to-[#0B0D11] p-5">
        <p className="font-heading font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-acid" /> Let OCTON draft this week</p>
        <p className="text-sm text-[#8A91A6] mt-1">Balanced across the week from your club's focus areas, starting {prettyDay(week > today() ? week : addDays(today(), 1), { weekday: "long", day: "numeric", month: "short" })}. Everything arrives as a draft: nothing goes live until you publish it.</p>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mt-3">
          <input className="md:col-span-3 h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" placeholder="Any focus? e.g. engine week, deload, comp prep" value={draft.focus} onChange={(e) => setDraft({ ...draft, focus: e.target.value })} />
          <select className="h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value })}>
            <option value="mixed">Mixed levels</option><option value="beginner">Beginners</option><option value="intermediate">Intermediate</option><option value="competitive">Competitive</option>
          </select>
          <select className="h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm" value={draft.days} onChange={(e) => setDraft({ ...draft, days: e.target.value })}>
            {[3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n} days</option>)}
          </select>
          <button disabled={busy} onClick={runDraft} data-testid="octon-draft" className="h-10 rounded-full bg-acid text-black font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : "Draft week"}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => setWeek(addDays(week, -7))} className="p-2 rounded-full border border-[#343A4A]" aria-label="Previous week"><ChevronLeft className="w-4 h-4" /></button>
        <p className="font-heading font-bold">{prettyDay(week, { day: "numeric", month: "short" })} to {prettyDay(addDays(week, 6), { day: "numeric", month: "short", year: "numeric" })}</p>
        <button onClick={() => setWeek(addDays(week, 7))} className="p-2 rounded-full border border-[#343A4A]" aria-label="Next week"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {days.map((d) => <DayColumn key={d} day={d} onAdd={(day) => setEditor({ date: day })} onEdit={(w) => setEditor(w)} />)}
      </div>
      <Feedback />
      <WodEditor open={!!editor} onOpenChange={(o) => !o && setEditor(null)} initial={editor} scope="club" onSaved={() => qc.invalidateQueries({ queryKey: ["wods"] })} />
    </div>
  );
}
